import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CustomDropdown from '@components/CustomDropdown';
import ImageUpload from '@components/ImageUpload';
import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess } from '@components/Toast';

import { currentStageColumns } from '@features/crm/utils/helpers';

import { fetchAllBrands, fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { validateEmail } from '@utils/Util';

const AddBrand = ({ isPopup = false, onAddSuccess }) => {
	const [loading, setLoading] = useState(false);
	const [brandTypes, setBrandTypes] = useState([]);
	const [brands, setBrands] = useState([]);
	const [brandInfo, setBrandInfo] = useState({});
	const [errors, setErrors] = useState({});
	const navigate = useNavigate();
	const location = useLocation();

	useEffect(() => {
		let brandName = new URLSearchParams(location.search).get('name');
		fetchAllBrands().then((res) => {
			let tmpBrands = res.map((item) => ({ id: item.id, value: item.name }));
			setBrands(tmpBrands);
		});
		setBrandInfo({
			status: { id: 'PIPELINE', value: 'Pipeline' },
			name: brandName
		});
		fetchEnumValues('info.nitex.app.brand.BrandType', setBrandTypes);
	}, []);

	const onBrandChange = (e) => {
		e.preventDefault();
		setErrors({
			...errors,
			brandNameError: ''
		});
		setBrandInfo({
			...brandInfo,
			[e.target.id]: e.target.value
		});
	};

	const onBrandTypeClick = (type, fieldName) => {
		setBrandInfo({
			...brandInfo,
			[fieldName]: { id: type.id, value: type.value }
		});
	};

	const checkValidation = () => {
		let err = {};
		if (!brandInfo?.logoDocumentDTO?.base64Str) {
			err.brandImageError = 'Brand logo is required';
		}
		if (!brandInfo.name) {
			err.brandNameError = 'Brand name is required';
		}
		if (!brandInfo.brandType?.id) {
			err.brandTypeError = 'Brand type is required';
		}
		if (!brandInfo.website) {
			err.websiteError = 'Website is required';
		}
		// if (!brandInfo.phone) {
		// 	err.phoneError = 'Phone is required';
		// }
		if (brandInfo.email && !validateEmail(brandInfo.email)) {
			err.emailError = 'Invalid email format!';
		}
		// if (!brandInfo.taxId) {
		// 	err.taxIdError = 'TaxID is required';
		// }
		setErrors(err);
		return Object.keys(err).length > 0;
	};

	const onAdd = async () => {
		if (!checkValidation()) {
			let body = {
				logoDocumentDTO: brandInfo.logoDocumentDTO,
				name: brandInfo.name,
				phone: brandInfo.phone,
				taxId: brandInfo.taxId,
				email: brandInfo.email,
				website: brandInfo.website,
				parentId: brandInfo?.motherBrand?.id,
				type: brandInfo?.brandType?.id,
				status: brandInfo?.status?.id
			};
			setLoading(true);
			await ApiClient.POST('addBrandInfo', body)
				.then(({ data }) => {
					setLoading(false);
					if (data && data.success) {
						toastSuccess(data.message);
						if (isPopup) {
							onAddSuccess(data);
						} else {
							navigate(`/brand/profile/${data.id}`);
						}
					}
				})
				.catch(({ response }) => {
					setLoading(false);
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
					} else {
						toastError('Request was unsuccessful.');
					}
				});
		}
	};

	const onUploadImage = (image) => {
		setBrandInfo({
			...brandInfo,
			logoDocumentDTO: image
		});
	};

	const handleReset = () => {
		setErrors({});
		setBrandInfo({});
	};

	return (
		<LoaderComponent loading={loading}>
			<div className={`${isPopup ? '' : 'brand-container'}`}>
				<div className="form-container new-brand">
					<div className="form-header pb-3">
						<p className="semibold-16">Add brand</p>
					</div>
					<div className="container px-0">
						<div className="row form-row">
							<div className="col-md-12">
								<h2 className="semibold-14 mb-0">Brand Basic info*</h2>
							</div>
							<div className="col-md-12">
								<div className="form-group">
									<div className="upload-design-image mb-4 mb-sm-0">
										{brandInfo?.logoDocumentDTO ? (
											<>
												<img
													className="brand-logo"
													src={
														brandInfo?.logoDocumentDTO?.base64Str
															? brandInfo?.logoDocumentDTO?.base64Str
															: ''
													}
													alt=""
												/>
												<ImageUpload
													onUploadImage={onUploadImage}
													docType={'ACCESSORIES_DESIGN'}
													type={'image'}
													isEditable={true}
												/>
											</>
										) : (
											<ImageUpload
												onUploadImage={onUploadImage}
												docType={'ACCESSORIES_DESIGN'}
												type={'image'}
											/>
										)}
									</div>
									{errors.brandImageError && (
										<p className="error">{errors.brandImageError}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pr-6">
								<div className="form-group">
									<label htmlFor="bname">Name*</label>
									<input
										type="text"
										autoComplete="off"
										placeholder="Type brand name"
										className="bg-gray-light border-0"
										name="name"
										id="name"
										value={brandInfo.name || ''}
										onChange={onBrandChange}
									/>
									{errors.brandNameError && (
										<p className="error">{errors.brandNameError}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group">
									<label htmlFor="type">Type</label>
									<CustomDropdown
										type="addItem"
										title={'Select'}
										isShowLabel={true}
										onItemClick={(value) => onBrandTypeClick(value, 'brandType')}
										items={brandTypes?.map((item) => ({
											id: item.id,
											value: item.label
										}))}
										selectedItem={brandInfo?.brandType?.value || ''}
									/>
									{errors.brandTypeError && (
										<p className="error">{errors.brandTypeError}</p>
									)}
								</div>
							</div>
							<div className="col-md-6 pr-6">
								<div className="form-group">
									<label htmlFor="website">website</label>
									<input
										type="text"
										autoComplete="off"
										id="website"
										value={brandInfo.website || ''}
										onChange={onBrandChange}
									/>
									{errors.websiteError && <p className="error">{errors.websiteError}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group">
									<label htmlFor="type">Current stage</label>
									<CustomDropdown
										type="addItem"
										title={'Select'}
										isShowLabel={true}
										onItemClick={(value) => onBrandTypeClick(value, 'status')}
										items={currentStageColumns?.map((item) => ({
											id: item.value,
											value: item.label
										}))}
										selectedItem={brandInfo?.status?.value || ''}
									/>
								</div>
							</div>
							<div className="col-md-6 pr-6">
								<div className="form-group">
									<label htmlFor="phone">Phone</label>
									<input
										type="number"
										autoComplete="off"
										id="phone"
										name="phone"
										value={brandInfo.phone || ''}
										onChange={onBrandChange}
									/>
									{errors.phoneError && <p className="error">{errors.phoneError}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group">
									<label htmlFor="email">Email</label>
									<input
										type="email"
										autoComplete="off"
										id="email"
										value={brandInfo.email || ''}
										onChange={onBrandChange}
									/>
									{errors.emailError && <p className="error">{errors.emailError}</p>}
								</div>
							</div>
							<div className="col-md-6 pr-6">
								<div className="form-group">
									<label htmlFor="taxId">TaxID</label>
									<input
										type="text"
										autoComplete="off"
										id="taxId"
										value={brandInfo.taxId || ''}
										onChange={onBrandChange}
									/>
									{errors.taxIdError && <p className="error">{errors.taxIdError}</p>}
								</div>
							</div>
							<div className="col-md-6 pl-6">
								<div className="form-group">
									<label htmlFor="taxId">Mother company (optional)</label>
									<CustomDropdown
										type="addItem"
										title={'Select'}
										isShowLabel={true}
										onItemClick={(value) => onBrandTypeClick(value, 'motherBrand')}
										items={brands}
										selectedItem={brandInfo?.motherBrand?.value || ''}
									/>
								</div>
							</div>
						</div>
						<div className="row">
							<div className="col-md-12 pb-3 text-right">
								<button className="button gray-outline size32 mr-12" onClick={handleReset}>
									Reset
								</button>
								<button className="button size32" onClick={() => onAdd()}>
									Submit
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default AddBrand;
